/**
 * Utilidades para manejo de fechas y calendario
 * @module dateHelpers
 */

/**
 * Verifica si una orden de trabajo está vencida
 * @param {string|Date} fechaProgramada - Fecha programada de la orden de trabajo
 * @param {string} estado - Estado de la orden de trabajo
 * @returns {boolean} true si está vencida
 */
export function isOrdenTrabajoVencida(fechaProgramada, estado) {
  if ((estado !== 'programado' && estado !== 'en_progreso') || !fechaProgramada) return false;

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  const fecha = new Date(fechaProgramada);
  if (isNaN(fecha.getTime())) return false; // Validar fecha inválida
  fecha.setHours(0, 0, 0, 0);

  return fecha < hoy;
}

/**
 * Calcula días hasta la orden de trabajo (negativo si vencida)
 * @param {string|Date} fechaProgramada - Fecha programada
 * @returns {number|null} Días de diferencia (negativo = vencida) o null si fecha inválida
 */
export function getDiasHastaOrdenTrabajo(fechaProgramada) {
  if (!fechaProgramada) return null;

  const fecha = new Date(fechaProgramada);
  if (isNaN(fecha.getTime())) return null;

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  fecha.setHours(0, 0, 0, 0);

  const diff = fecha - hoy;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * Obtiene clave de fecha en formato YYYY-MM-DD
 * @param {Date|string} date - Fecha a convertir
 * @returns {string|null} Fecha en formato YYYY-MM-DD o null si es inválida
 */
export function getDateKey(date) {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Agrupa órdenes de trabajo por fecha
 * @param {Array} ordenesTrabajo - Array de órdenes de trabajo
 * @returns {Object} Objeto con keys YYYY-MM-DD y valores array de órdenes
 */
export function groupOrdenesByDate(ordenesTrabajo) {
  return ordenesTrabajo.reduce((acc, mant) => {
    const key = getDateKey(mant.fecha_ejecucion);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(mant);
    return acc;
  }, {});
}

/**
 * Genera array de 42 días para grid de calendario (6 semanas × 7 días)
 * @param {number} year - Año
 * @param {number} month - Mes (0-11, estilo JavaScript)
 * @returns {Array<Object>} Array de objetos día
 */
export function getCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const startDate = new Date(firstDay);

  // Retroceder al lunes de la semana
  const dayOfWeek = startDate.getDay();
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  startDate.setDate(startDate.getDate() - daysToSubtract);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days = [];
  for (let i = 0; i < 42; i++) {
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + i);

    days.push({
      date: new Date(currentDate),
      day: currentDate.getDate(),
      isCurrentMonth: currentDate.getMonth() === month,
      isToday: currentDate.getTime() === today.getTime(),
      ordenesTrabajo: []
    });
  }

  return days;
}

/**
 * Compara si dos fechas son el mismo día
 * @param {Date|string} date1 - Primera fecha
 * @param {Date|string} date2 - Segunda fecha
 * @returns {boolean} true si son el mismo día
 */
export function isSameDay(date1, date2) {
  return getDateKey(date1) === getDateKey(date2);
}

/**
 * Obtiene nombre del mes en español
 * @param {number} month - Mes (0-11)
 * @returns {string} Nombre del mes
 */
export function getMonthName(month) {
  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  return meses[month];
}

/**
 * Obtiene array de nombres de días de la semana
 * @returns {Array<string>} Array de días
 */
export function getWeekDays() {
  return ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
}
